'use client';

import React from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { Bundle } from '@/ui/services/BundleService';
import { useCartStore } from '@/ui/store/cart';

export interface BundleCardProps {
  bundle: Bundle;
  /** Page of the bundle itself. Without one, the card runs the action of its "+". */
  pdpPath?: string;
}

// Shared with the product cards of the same grid so both actions look alike.
// `z-10` lifts the "+" above the stretched card target, so it keeps its own
// action instead of the one of the card.
export const ACTION_BUTTON_CLASSES =
  'absolute bottom-3 right-3 z-10 flex h-10 w-10 items-center justify-center rounded-full ' +
  'border border-neutral-100 bg-primary-100 text-3xl font-bold leading-none text-neutral-100 ' +
  'duration-200 hover:bg-white';

export const CARD_IMAGE_FRAME_CLASSES = 'relative rounded-2xl border border-neutral-10 bg-white p-4';

// The card name is the one target of the card. Its pseudo element stretches over
// the whole card, so a click on the image or the text opens the same target
// without a second link in the tab order and without nesting the "+" inside it.
// The focus ring sits on the stretched area too, so the keyboard focus marks the
// whole card.
export const CARD_TARGET_CLASSES =
  'text-left after:absolute after:inset-0 after:rounded-2xl focus-visible:outline-none ' +
  'focus-visible:after:outline focus-visible:after:outline-2 ' +
  'focus-visible:after:outline-offset-2 focus-visible:after:outline-[#406AFF]';

/**
 * One bundle offer: image, "+" action, name, the products it contains and the
 * discounted price. The action adds the bundle to the cart as the editor
 * configured it. An upgrade bundle instead opens the chooser with the bundle
 * preselected, because it sells only after the device check the chooser runs.
 * A bundle with selectable variants opens its page, where the shopper picks
 * them; without a page it adds its defaults like any other bundle.
 * A click on the rest of the card opens the page of the bundle, or runs the
 * same action as the "+" while the bundle has no page.
 */
export function BundleCard({ bundle, pdpPath }: Readonly<BundleCardProps>) {
  const t = useTranslations('ClassicProductDetail');
  const tBundle = useTranslations('BundleOption');
  const { formatCurrency } = useCurrencyFormatter();
  const { localizedLink } = useLocaleLink();
  const cartStore = useCartStore();

  // Only an upgrade bundle needs the chooser, for the device check. A bundle
  // without a Smart Lock has no chooser at all and always adds directly.
  const chooserPath = bundle.isUpgradeBundle ? bundle.getChooserPath() : undefined;
  // Variants are picked on the page of the bundle, so a configurable bundle
  // with a page sends its "+" there instead of adding the defaults.
  const isConfigurable = bundle.hasSelectableSlots();
  const pagePathForAction = isConfigurable ? pdpPath : undefined;
  const actionPath = chooserPath ?? pagePathForAction;
  const productNames = bundle.getProductNames(' + ');
  const hasDiscount = bundle.totalDiscount > 0;
  const actionLabel = t('chooseBundle', { name: bundle.name });

  // The bundle sells as the editor configured it, so the card adds its default
  // variants straight to the cart, like the product-cards tile does.
  const addDefaultVariantsToCart = () => {
    bundle.checkStockAndAddToCart(bundle.defaultBundleProductVariants, cartStore);
  };

  // The page of the bundle wins; without one the card does what its "+" does.
  const cardTargetPath = pdpPath ?? chooserPath;

  return (
    <article data-testid="classic-pdp-bundle-card" className="relative flex h-full flex-col">
      <div data-testid="classic-pdp-card-image-frame" className={CARD_IMAGE_FRAME_CLASSES}>
        <OptimizedImage
          image={bundle.firstImage}
          width={300}
          height={300}
          sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 80vw"
          className="mx-auto h-40 w-full object-contain"
        />
        {actionPath ? (
          <Link
            href={localizedLink(actionPath)}
            scroll={!chooserPath}
            aria-label={actionLabel}
            className={ACTION_BUTTON_CLASSES}
          >
            +
          </Link>
        ) : (
          <button
            type="button"
            onClick={addDefaultVariantsToCart}
            aria-label={actionLabel}
            className={ACTION_BUTTON_CLASSES}
          >
            +
          </button>
        )}
      </div>

      <h3 className="mt-3 font-bold text-neutral-100">
        {cardTargetPath ? (
          <Link
            href={localizedLink(cardTargetPath)}
            scroll={!!pdpPath}
            className={CARD_TARGET_CLASSES}
          >
            {bundle.name}
          </Link>
        ) : (
          <button type="button" onClick={addDefaultVariantsToCart} className={CARD_TARGET_CLASSES}>
            {bundle.name}
          </button>
        )}
      </h3>
      {/* Same precedence as the chooser: an editorial description wins over the plain product list. */}
      {bundle.description ? (
        <RichTextRenderer className="text-sm text-neutral-55" blocks={bundle.description} />
      ) : (
        productNames && <p className="text-sm text-neutral-55">{productNames}</p>
      )}

      {/* Pinned to the bottom so the prices of neighbouring cards line up. */}
      <p className="mt-auto flex flex-wrap items-baseline gap-x-2 pt-1">
        {hasDiscount && (
          <span className="text-sm text-[#EC041B] line-through">
            {formatCurrency(bundle.totalOriginalPrice)}
          </span>
        )}
        <span className="font-bold">{formatCurrency(bundle.finalPrice)}</span>
        {hasDiscount && (
          <span className="ml-auto text-sm font-semibold text-[#147E47]">
            {tBundle('Save')} {formatCurrency(bundle.totalDiscount)}
          </span>
        )}
      </p>
    </article>
  );
}
